/**
 * One final-round chain option and its per-segment agenda, extracted from
 * FinalRoundsTab so that file stays under the line cap and the chain-row markup
 * is reusable (the tab shows the top options plus a "show more" set).
 *
 * The agenda is a LIST, not one arrow-joined line — each move is attributable (a
 * chain is 2–3 conversations, usually only one has the conflict; conflicts carry
 * a `segmentKey` so each line names its own).
 */
import { Checkbox } from '@finn-auto/design-system/atoms/checkbox';
import { cn } from '../../../../shared';
import type { ChainOption, ChainSegment, SlotConflict } from '../../types/taActionBoard';
import { COLORS, firstNameFromEmail, fmtSlotRange, fmtTime, slotNumber } from './format';

export function ChainRow({
  chain,
  index,
  checked,
  onToggle,
}: {
  chain: ChainOption;
  index: number;
  checked: boolean;
  onToggle: () => void;
}) {
  const moves = chain.conflicts.length;
  return (
    <li
      className={cn(
        'rounded-xl bg-white px-2.5 py-2 text-xs text-gray-800 ring-1 transition-colors',
        checked ? 'ring-gray-400' : 'ring-gray-100'
      )}
    >
      <div className="flex items-start gap-2">
        <span className="pt-0.5">
          <Checkbox checked={checked} onCheckedChange={onToggle} aria-label={`Propose ${fmtSlotRange(chain)}`} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span>{slotNumber(index)}</span>
            <span role="img" aria-label={chain.grade === 'free' ? 'Free' : 'Needs a meeting moved'} title={chain.grade === 'free' ? 'Free' : 'Needs a meeting moved'} className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: chain.grade === 'free' ? COLORS.statusGreen : COLORS.statusOrange }} />
            <span className="font-semibold">{fmtSlotRange(chain)}</span>
            {moves > 0 && (
              <span className="text-gray-500">
                {moves} meeting{moves === 1 ? '' : 's'} would move
              </span>
            )}
          </div>
          <ul className="mt-1 space-y-0.5 text-[11px] text-gray-600">
            {chain.segments.map((segment) => (
              <SegmentLine
                key={segment.key}
                segment={segment}
                conflicts={chain.conflicts.filter((c) => c.segmentKey === segment.key)}
              />
            ))}
            {/* A conflict whose segment is not in this list must still be shown.
                Attributing per segment silently drops anything unattributable —
                and "someone has to move a meeting" is the one detail that must
                never disappear from an option the recruiter is about to offer. */}
            {orphanConflicts(chain).map((c) => (
              <li key={`${c.email}-${c.title}`} style={{ color: COLORS.orangeText }}>
                {firstNameFromEmail(c.email)} moves “{c.title}”
              </li>
            ))}
          </ul>
        </div>
      </div>
    </li>
  );
}

/** Conflicts this chain reports that no listed segment accounts for. */
function orphanConflicts(chain: ChainOption): (SlotConflict & { segmentKey: string })[] {
  const keys = new Set(chain.segments.map((s) => s.key));
  return chain.conflicts.filter((c) => !keys.has(c.segmentKey));
}

/** "09:00 Final interview (Nora) — Nora moves “Weekly sync”", plus its break. */
function SegmentLine({
  segment,
  conflicts,
}: {
  segment: ChainSegment;
  conflicts: (SlotConflict & { segmentKey: string })[];
}) {
  return (
    <>
      {/* A real break is its own line: it is time the candidate is waiting, not
          part of either conversation. */}
      {segment.breakBefore && <li className="text-gray-400">{segment.gapBefore} min break</li>}
      <li className="flex flex-wrap items-baseline gap-x-1.5">
        <span className="font-medium text-gray-700">{fmtTime(segment.start)}</span>
        <span>{segment.label}</span>
        <span className="text-gray-500">({segment.participants.map(firstNameFromEmail).join(', ')})</span>
        {conflicts.length > 0 && (
          <span style={{ color: COLORS.orangeText }}>
            ({conflicts.map((c) => `${firstNameFromEmail(c.email)} moves “${c.title}”`).join('; ')})
          </span>
        )}
      </li>
    </>
  );
}
